import {createSlice} from '@reduxjs/toolkit';
import impersonationData from '../components/Dashboard/impersonation.json';

const initialState = {
  token: null,
  userName: null,
  emailId: null,
  embedUrl: null,
  userRole: [''],
  roleChanged: false,
  roleDefaultEmail: null as string | null,
  categoryList: [''],
  sectorList: [''],
  sessionChecking: true,
  chatToggle: 'chat',
  impersonatedEmail: null,
};

const managerRoles = [
  'Manager User',
  'Individual / Manager User',
  'Individual/Manager User',
  'SpendwiseUser',
];

const hasOnlyManagerRoles = (userRole: unknown) =>
  Array.isArray(userRole) &&
  userRole.length > 0 &&
  userRole.every(
    (role) => typeof role === 'string' && managerRoles.includes(role.trim()),
  );

// Impersonation comes from the Okta role or from impersonation.json.
const isImpersonationUser = (userRole: unknown, email: unknown) =>
  (Array.isArray(userRole) && userRole.includes('Impersonation User')) ||
  (typeof email === 'string' &&
    Object.keys(impersonationData[0] ?? {}).some(
      (key) => key.toLowerCase() === email.toLowerCase(),
    ));

// Only manager roles (no Sector/Category/Super User/impersonation): Manager Mode is always ON and the toggle is hidden.
export const isManagerOnlyUser = (userRole: unknown, email: unknown) =>
  hasOnlyManagerRoles(userRole) && !isImpersonationUser(userRole, email);

export const OktaAuthSlice = createSlice({
  name: 'oktaAuth',
  initialState,
  reducers: {
    setAuthToken: (state, action) => {
      sessionStorage.setItem('accessToken', action.payload);
      state.token = action.payload;
    },
    setUserName: (state, action) => {
      state.userName = action.payload;
    },
    setUserEmailId: (state, action) => {
      state.emailId = action.payload;
    },
    setEmbedUrl: (state, action) => {
      state.embedUrl = action.payload;
    },
    setUserRole: (state, action) => {
      state.userRole = action.payload;
      if (Array.isArray(action.payload) && action.payload.length > 0) {
        const managerOnly = isManagerOnlyUser(action.payload, state.emailId);
        // Other users get the default once per user; the store is persisted across sessions.
        if (managerOnly || state.roleDefaultEmail !== state.emailId) {
          state.roleChanged = managerOnly;
          state.roleDefaultEmail = state.emailId;
        }
      }
    },
    setRoleChanged: (state, action) => {
      state.roleChanged = isManagerOnlyUser(state.userRole, state.emailId)
        ? true
        : action.payload;
    },
    setCategoryList: (state, action) => {
      state.categoryList = action.payload;
    },
    setSectorList: (state, action) => {
      state.sectorList = action.payload;
    },
    setSessionChecking: (state, action) => {
      state.sessionChecking = action.payload;
    },
    setChatToggle: (state, action) => {
      state.chatToggle = action.payload;
    },
    setImpersonatedEmail: (state, action) => {
      state.impersonatedEmail = action.payload;
    },
    clearAuthStore: (state) => {
      state.token = null;
      state.userName = null;
      state.emailId = null;
      state.userRole = [];
      state.roleChanged = false;
      state.roleDefaultEmail = null;
      state.categoryList = [''];
      state.sectorList = [''];
      state.chatToggle = 'chat';
      state.impersonatedEmail = null;
    },
  },
});

export const {
  setAuthToken,
  setUserName,
  setUserEmailId,
  setEmbedUrl,
  clearAuthStore,
  setUserRole,
  setRoleChanged,
  setCategoryList,
  setSectorList,
  setSessionChecking,
  setChatToggle,
  setImpersonatedEmail,
} = OktaAuthSlice.actions;

export const getAuthToken = (state: any) => state.oktaAuth.token;

export const getUserName = (state: any) => state.oktaAuth.userName;

export const getUserEmail = (state: any) => state?.oktaAuth?.emailId;

export const getEmbedUrl = (state: any) => state.oktaAuth.embedUrl;

export const getUserRole = (state: any) => state.oktaAuth.userRole;

export const getRoleChanged = (state: any) => state.oktaAuth.roleChanged;

export const getCategoryList = (state: any) => state.oktaAuth.categoryList;

export const getSectorList = (state: any) => state.oktaAuth.sectorList;

export const getSessionChecking = (state: any) =>
  state.oktaAuth.sessionChecking;

export const getChatToggle = (state: any) => state.oktaAuth.chatToggle;
export const getImpersonatedEmail = (state: any) =>
  state.oktaAuth.impersonatedEmail;

export default OktaAuthSlice.reducer;
